import { useState } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useCollections } from '../../../features/products/hooks/useMetaData'
import { metaDataApi, type Collection, type CollectionPayload } from '../../../features/products/api/metaDataApi'
import { MetaTable } from '../components/MetaTable'
import { CollectionForm } from '../components/CollectionForm'
import { describeDiscount, describeWindow, discountState, hasDiscount } from '../collectionDiscount'

const STATE_LABEL = { active: 'Active', scheduled: 'Scheduled', ended: 'Ended' } as const

export const CollectionsTab = () => {
  const { data, isLoading } = useCollections()
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['collections'] })
    // Collection discounts change what products are worth, so drop those too.
    queryClient.invalidateQueries({ queryKey: ['products'] })
  }

  const createMutation = useMutation({
    mutationFn: (payload: CollectionPayload) => metaDataApi.createCollection(payload),
    onSuccess: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CollectionPayload }) =>
      metaDataApi.updateCollection(id, payload),
    onSuccess: () => {
      invalidate()
      setEditingId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => metaDataApi.deleteCollection(id),
    onSuccess: invalidate,
  })

  const items = (data?.data ?? []) as unknown as Record<string, unknown>[]

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'slug', label: 'Slug', render: (item: Record<string, unknown>) => (item.slug as string) ?? '—' },
    { key: 'productCount', label: 'Products', render: (item: Record<string, unknown>) => String(item.productCount ?? 0) },
    {
      key: 'discount',
      label: 'Discount',
      render: (item: Record<string, unknown>) => {
        const c = item as unknown as Collection
        if (!hasDiscount(c)) return <span className="text-muted-foreground">—</span>
        const window = describeWindow(c)
        const state = discountState(c)
        return (
          <div className="flex flex-col">
            <span className={state === 'ended' ? 'text-muted-foreground line-through' : 'text-foreground'}>
              {describeDiscount(c)}
            </span>
            {window && (
              <span className="text-[12px] text-muted-foreground">
                {STATE_LABEL[state]} · {window}
              </span>
            )}
          </div>
        )
      },
    },
  ]

  const renderAddForm = (onDone: () => void) => (
    <CollectionForm
      isLoading={createMutation.isPending}
      onSubmit={async (payload) => {
        await createMutation.mutateAsync(payload)
        onDone()
      }}
      onCancel={onDone}
    />
  )

  const renderEditForm = (item: Record<string, unknown>, onDone: () => void) => (
    <CollectionForm
      collection={item as unknown as Collection}
      isLoading={updateMutation.isPending}
      onSubmit={(payload) => updateMutation.mutateAsync({ id: item.id as string, payload })}
      onCancel={onDone}
    />
  )

  return (
    <MetaTable
      title="Collections"
      items={items}
      columns={columns}
      isLoading={isLoading}
      addLabel="Add Collection"
      renderAddForm={renderAddForm}
      onEdit={(item) => setEditingId(editingId === (item.id as string) ? null : (item.id as string))}
      onDelete={(item) => deleteMutation.mutate(item.id as string)}
      editingId={editingId}
      renderEditForm={renderEditForm}
    />
  )
}
